import contactbg from "@/assets/contactimg.png";
import whatsappicon from "@/assets/icons8-whatsapp-50.png";
import contactvec1 from "@/assets/Contactvector1.png";
import contactvec2 from "@/assets/Contactvector2.png";
import contactvec3 from "@/assets/Contactvector3.png";
import contactvec4 from "@/assets/Contactvector4.png";
import { useRef, useState, useEffect } from "react";
import emailjs from "emailjs-com";

export default function ContactPage() {
  const form = useRef<HTMLFormElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    success?: boolean;
    message?: string;
  }>({});

  // SEO Meta Tags Setup
  useEffect(() => {
    // Set page title
    document.title = "Contact Oaktree Financial Services - Financial Advisor Mumbai | Book Consultation";
    
    // Meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", "Contact Oaktree Financial Services in Mumbai for expert financial planning consultation. Call +91-9892303565 or visit us at Powai Plaza. Book your free financial advisory session today.");
    } else {
      const meta = document.createElement('meta');
      meta.name = "description";
      meta.content = "Contact Oaktree Financial Services in Mumbai for expert financial planning consultation. Call +91-9892303565 or visit us at Powai Plaza. Book your free financial advisory session today.";
      document.head.appendChild(meta);
    }

    // Meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute("content", "contact financial advisor Mumbai, book financial consultation, financial planner Powai, investment advisor contact, wealth management consultation, certified financial planner Mumbai contact, financial planning appointment, retirement planning consultation Mumbai");
    } else {
      const meta = document.createElement('meta');
      meta.name = "keywords";
      meta.content = "contact financial advisor Mumbai, book financial consultation, financial planner Powai, investment advisor contact, wealth management consultation, certified financial planner Mumbai contact, financial planning appointment, retirement planning consultation Mumbai";
      document.head.appendChild(meta);
    }

    // Open Graph Meta Tags
    const ogTags = [
      { property: "og:title", content: "Contact Oaktree Financial Services - Book Your Financial Consultation" },
      { property: "og:description", content: "Get in touch with Mumbai's trusted financial advisor. Expert financial planning, investment advisory, and wealth management consultation." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://www.oaktree.in/contact" },
    ];

    ogTags.forEach(tag => {
      let ogMeta = document.querySelector(`meta[property="${tag.property}"]`);
      if (ogMeta) {
        ogMeta.setAttribute("content", tag.content);
      } else {
        const meta = document.createElement('meta');
        meta.setAttribute("property", tag.property);
        meta.content = tag.content;
        document.head.appendChild(meta);
      }
    });

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute("href", "https://www.oaktree.in/contact");
    } else {
      const link = document.createElement('link');
      link.rel = "canonical";
      link.href = "https://www.oaktree.in/contact";
      document.head.appendChild(link);
    }

    // Structured Data for Contact Page
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contact Oaktree Financial Services",
      "description": "Contact information for Oaktree Financial Services in Mumbai",
      "mainEntity": {
        "@type": "FinancialService",
        "name": "Oaktree Financial Services",
        "telephone": "+91-9892303565",
        "email": "uma@oaktree.in",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "160 Powai Plaza Hiranandani Business Park Powai",
          "addressLocality": "Mumbai",
          "postalCode": "400076",
          "addressCountry": "IN"
        },
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "09:00",
            "closes": "18:00"
          },
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": "Saturday",
            "opens": "10:00",
            "closes": "14:00"
          }
        ]
      }
    };

    let script = document.querySelector('script[type="application/ld+json"]');
    if (script) {
      script.textContent = JSON.stringify(structuredData);
    } else {
      const newScript = document.createElement('script');
      newScript.type = "application/ld+json";
      newScript.textContent = JSON.stringify(structuredData);
      document.head.appendChild(newScript);
    }
  }, []);

  const sendEmail = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.current) return;

    setIsSubmitting(true);
    setSubmitStatus({});

    emailjs
      .sendForm(
        "service_w8xj9yd",
        "template_skmpcyc",
        form.current,
        "CZpE4KM--mNL6RdSd"
      )
      .then(
        (result) => {
          setSubmitStatus({
            success: true,
            message: "Message sent successfully!",
          });
          form.current?.reset();
        },
        (error) => {
          setSubmitStatus({
            success: false,
            message: "Failed to send message. Please try again.",
          });
        }
      )
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <div>
      <header
        className="relative bg-cover bg-center h-screen text-white"
        style={{
          backgroundImage: `url(${contactbg})`,
        }}
      >
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4">
          <h1 className="text-3xl lg:text-7xl md:text-6xl font-bold text-[#78A742]">
            Start Your Financial Journey <br />– Ready to take the first step?
          </h1>
          <p className="mt-4 text-black text-lg lg:text-2xl md:text-2xl max-w-3xl">
            Have questions or ready to take the first step? We're here to help
            you with expert guidance and a personalized approach.
          </p>
          <a
            href="https://wa.me/919892147989"
            target="_blank"
            rel="noopener noreferrer"
            className="fixed z-[9999] bottom-6 right-4 sm:bottom-8 sm:right-8 bg-green-600 hover:bg-green-700 text-black font-bold text-lg px-5 py-2 rounded-full flex items-center space-x-2 shadow-md"
            aria-label="Chat with financial advisor on WhatsApp"
          >
            <span>Chat Now</span>
            <img src={whatsappicon} className="w-8" alt="WhatsApp contact" />
          </a>
        </div>
      </header>

      <section className="min-h-screen relative bg-white flex items-center justify-center mx-auto m-18">
        <img
          src={contactvec1}
          alt="Contact decoration top left"
          className="hidden lg:block absolute top-15 left-0 w-48 md:w-80 z-20"
        />
        <img
          src={contactvec2}
          alt="Contact decoration top right"
          className="hidden lg:block absolute top-10 right-0 w-48 md:w-75 z-20"
        />
        <img
          src={contactvec3}
          alt="Contact decoration bottom left"
          className="hidden lg:block absolute bottom-6 left-0 w-48 md:w-75 z-20"
        />
        <img
          src={contactvec4}
          alt="Contact decoration bottom right"
          className="hidden lg:block absolute bottom-0 right-0 w-48 md:w-75 z-20"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-12 text-center w-full lg:w-3/4 z-10 m-2 lg:m-25">
          <article className="bg-[#92CD4D] m-2 p-12 rounded-xl shadow-md flex items-center justify-center flex-col">
            <h2 className="mb-6 text-2xl lg:text-3xl font-bold">
              📧 Email Us
            </h2>
            <address className="not-italic">
              <ul>
                <li className="text-lg underline">
                  <a href="mailto:uma@oaktree.in">uma@oaktree.in</a>
                </li>
                <li className="text-lg underline">
                  <a href="mailto:uma_velan@yahoo.com">uma_velan@yahoo.com</a>
                </li>
              </ul>
            </address>
          </article>
          
          <article className="bg-[#92CD4D] m-2 p-12 rounded-xl shadow-md flex items-center justify-center flex-col">
            <h2 className="mb-6 text-2xl lg:text-3xl font-bold">📞 Call Us</h2>
            <address className="not-italic">
              <ul className="space-y-1">
                <li>
                  <a href="tel:+91-9892303565">+91-9892303565</a>
                </li>
              </ul>
            </address>
          </article>
          
          <article className="bg-[#92CD4D] m-2 p-12 rounded-xl shadow-md flex items-center justify-center flex-col">
            <h2 className="mb-6 text-2xl lg:text-3xl font-bold">
              📍 Office Address
            </h2>
            <address className="not-italic">
              <p>
                160 Powai Plaza <br /> Hiranandani Business Park <br /> Powai
                Mumbai-400076
              </p>
            </address>
          </article>
          
          <article className="bg-[#92CD4D] m-2 p-12 rounded-xl shadow-md flex items-center justify-center flex-col">
            <h2 className="mb-6 text-2xl lg:text-3xl font-bold">
              🕒 Working Hours
            </h2>
            <p>
              Monday – Friday: 9 AM – 6 PM
              <br />
              Saturday: 10 AM – 2 PM
              <br />
              Sunday: Closed
            </p>
          </article>
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-30 items-start px-3 lg:px-40 py-0 lg:py-30">
        <div className="justify-items-start max-w-full">
          <h2 className="text-3xl lg:text-7xl font-bold text-[#49BC31] mb-1 lg:mb-5">
            Have questions?
          </h2>
          <p className="text-3xl lg:text-4xl font-bold text-black mb-3">
            We'd love to hear from you – we'll get back to you as soon as we can
          </p>

          <address className="not-italic">
            <div className="flex items-center mt-20 mb-4">
              <span className="bg-yellow-100 p-2 rounded-full text-yellow-500 mr-3" aria-hidden="true">
                📞
              </span>
              <a href="tel:+919892303565" className="text-base text-black font-semibold">
                9892303565 / +91 89289 93593
              </a>
            </div>
            <div className="flex items-center mb-4">
              <span className="bg-yellow-100 p-2 rounded-full text-yellow-500 mr-3" aria-hidden="true">
                ✉️
              </span>
              <a href="mailto:uma@oaktree.in" className="text-base text-black font-semibold">
                uma@oaktree.in / www.oaktree.in
              </a>
            </div>
            <div className="flex items-center">
              <span className="bg-yellow-100 p-2 rounded-full text-yellow-500 mr-3" aria-hidden="true">
                📍
              </span>
              <span className="text-sm lg:text-base text-black font-semibold">
                160 Powai Plaza Hiranandani Business Park Powai Mumbai-400076
              </span>
            </div>
          </address>
        </div>

        {/* Contact Form */}
        <form
          ref={form}
          onSubmit={sendEmail}
          className="bg-white rounded-lg mb-4"
          aria-label="Contact form for financial consultation"
        >
          <div className="grid grid-cols-1 gap-4 mb-4">
            <div className="flex flex-col mb-4">
              <label
                htmlFor="name"
                className="mb-2 text-lg font-bold text-black"
              >
                Name
              </label>
              <input
                id="name"
                name="user_name"
                type="text"
                className="bg-[#E0D6AD] p-3 rounded w-full"
                required
                aria-required="true"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="flex flex-col">
                <label
                  htmlFor="email"
                  className="text-lg font-bold text-black mb-2"
                >
                  Email
                </label>
                <input
                  id="email"
                  name="user_email"
                  type="email"
                  className="bg-[#E0D6AD] p-3 rounded w-full"
                  required
                  aria-required="true"
                />
              </div>
              <div className="flex flex-col">
                <label
                  htmlFor="phone"
                  className="mb-2 text-lg font-bold text-black"
                >
                  Phone Number
                </label>
                <input
                  id="phone"
                  name="user_phone"
                  type="tel"
                  className="bg-[#E0D6AD] p-3 rounded w-full"
                  required
                  aria-required="true"
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col mb-4">
            <label
              htmlFor="message"
              className="mb-2 text-lg font-bold text-black"
            >
              Message
            </label>
            <textarea
              id="message"
              name="message"
              className="bg-[#E0D6AD] p-3 rounded w-full h-32"
              required
              aria-required="true"
            ></textarea>
          </div>

          {submitStatus.message && (
            <div
              className={`p-3 rounded mb-4 ${
                submitStatus.success
                  ? "bg-green-100 text-green-700"
                  : "bg-red-100 text-red-700"
              }`}
              role="alert"
            >
              {submitStatus.message}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className={`bg-yellow-400 px-6 py-2 rounded hover:bg-yellow-500 font-semibold mt-8 ${
              isSubmitting ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isSubmitting ? "SENDING..." : "SUBMIT NOW"}
          </button>
        </form>
      </section>
    </div>
  );
}